import type { PublishBlocker } from '@repo/api-contracts';
import { type MutationError, trpc } from '@/shared/api';

/** 발행 결과 — 성공이면 미충족 사유가 존재할 수 없다는 상호배타를 타입으로 고정한다. */
type PublishOutcome =
  | { readonly published: true }
  | { readonly published: false; readonly unmetConditions: readonly PublishBlocker[] };

interface UsePublishProductOptions {
  readonly onSuccess?: () => void;
}

interface UsePublishProductResult {
  /** 발행 요청 — 가드 미충족이면 사유가 담긴 결과, 네트워크 오류면 null. */
  readonly publish: (id: string) => Promise<PublishOutcome | null>;
  readonly isPending: boolean;
  readonly error: MutationError | null;
  /** 지난 오류를 지운다 — 같은 오류 줄을 쓰는 다른 요청을 시작할 때 옛 오류가 남지 않게. */
  readonly reset: () => void;
}

/** 상품 발행(공개 전환). 발행 가드는 서버가 검증한다. */
export const usePublishProduct = ({
  onSuccess,
}: UsePublishProductOptions = {}): UsePublishProductResult => {
  const mutation = trpc.productManagement.products.publish.useMutation({ onSuccess });

  const publish = async (id: string): Promise<PublishOutcome | null> => {
    const result = await mutation.mutateAsync({ id }).catch(() => null);

    if (result === null) {
      return null;
    }

    return result.published
      ? { published: true }
      : { published: false, unmetConditions: result.unmetConditions };
  };

  return { publish, isPending: mutation.isPending, error: mutation.error, reset: mutation.reset };
};
